// row 113 Grip and useSortable: the 20 px six-dot handle (--text-3, --text-2 on hover) of a sortable round column, skill
// row or criterion. It is a button with aria-roledescription="sortable", named by the item it moves; ↑/← moves the
// item one place earlier and ↓/→ one place later (Home/End to the ends), the move is announced in a polite live
// region, and focus stays on the moved item's grip. The moved item settles in var(--t) (200 ms) unless motion is off
// (reduced motion or capture). No pointer drag is drawn (DC-13); the keys are the reorder.

import { forwardRef, useCallback, useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import { Icon } from "../core/icons";
import { cx } from "../lib/cx";
import { useHs, type Hs } from "../lib/hs";
import { useMc } from "../lib/McRoot";
import { useIsoLayoutEffect } from "../lib/useIsoLayoutEffect";
import { G1_LABELS } from "./labels";

export interface GripProps {
  /** The item it moves (a round's or a skill's name): the handle's accessible name. */
  label: string;
  onKeyDown?: ((event: KeyboardEvent<HTMLButtonElement>) => void) | undefined;
  /** A rule keeps the order fixed: the handle is not drawn (DC-24, absent). */
  can?: boolean | undefined;
  hs?: Hs | undefined;
  className?: string | undefined;
}

export const Grip = forwardRef<HTMLButtonElement, GripProps>(function Grip({ label, onKeyDown, can, hs, className }, ref) {
  const hot = useHs(hs);
  if (can === false) return null;
  return (
    <button
      ref={ref}
      type="button"
      className={cx("mc-grip", className)}
      aria-roledescription={G1_LABELS.sortable}
      aria-label={label}
      onKeyDown={onKeyDown}
      {...hot}
    >
      <Icon name="grip" size={20} />
    </button>
  );
});

export interface SortableOptions<K extends string> {
  /** The items' keys, in their current order. */
  keys: readonly K[];
  /** Moves the item at `from` to `to` (the host reorders its list). */
  onMove: (from: number, to: number) => void;
  /** An item's name, for the announcement. */
  name: (key: K) => string;
}

export interface Sortable<K extends string> {
  /** The props of an item's Grip (spread onto <Grip label=…>). */
  handle: (key: K) => { ref: (element: HTMLButtonElement | null) => void; onKeyDown: (event: KeyboardEvent<HTMLButtonElement>) => void };
  /** The class of an item that has just moved (its 200 ms settle). */
  movedClass: (key: K) => string | undefined;
  /** The polite live region: render it once, beside the list. */
  status: ReactNode;
}

export function useSortable<K extends string>({ keys, onMove, name }: SortableOptions<K>): Sortable<K> {
  const { reducedMotion } = useMc();
  const grips = useRef(new Map<K, HTMLButtonElement>());
  const [moved, setMoved] = useState<{ key: K; tick: number } | null>(null);
  const [said, setSaid] = useState("");
  const refocus = useRef<K | null>(null);

  // React moves the item's node; a node that leaves the document loses focus, so the grip takes it back.
  useIsoLayoutEffect(() => {
    const key = refocus.current;
    if (key === null) return;
    refocus.current = null;
    const grip = grips.current.get(key);
    if (grip && grip.ownerDocument.activeElement !== grip) grip.focus();
  }, [keys]);

  const move = useCallback((key: K, delta: number | "start" | "end") => {
    const from = keys.indexOf(key);
    if (from < 0) return;
    const last = keys.length - 1;
    const to = delta === "start" ? 0 : delta === "end" ? last : Math.max(0, Math.min(last, from + delta));
    if (to === from) return;
    refocus.current = key;
    onMove(from, to);
    setMoved((previous) => ({ key, tick: (previous?.tick ?? 0) + 1 }));
    setSaid(`${name(key)} · ${to + 1} / ${keys.length}`);
  }, [keys, onMove, name]);

  return {
    handle: (key) => ({
      ref: (element) => { if (element) grips.current.set(key, element); else grips.current.delete(key); },
      onKeyDown: (event) => {
        const delta = event.key === "ArrowUp" || event.key === "ArrowLeft" ? -1
          : event.key === "ArrowDown" || event.key === "ArrowRight" ? 1
            : event.key === "Home" ? "start" : event.key === "End" ? "end" : null;
        if (delta === null) return;
        event.preventDefault();
        move(key, delta);
      },
    }),
    movedClass: (key) => (moved && moved.key === key && !reducedMotion ? (moved.tick % 2 ? "mc-sort-moved" : "mc-sort-moved-b") : undefined),
    status: <span className="mc-sr" role="status" aria-live="polite">{said}</span>,
  };
}
